import React, { createContext, useContext } from 'react';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useVisualViewport } from '../../useVisualViewport';
import { MeasuredCookieType } from '../redux/types';
import {
  useMeasureElementCookieState,
  DEFAULT_SIZE,
} from '../useMeasureElementCookieState';
import { getEnv } from '@wix/da-react-app/pkg/client/Environment/redux/selectors';
import { useSelector } from 'react-redux';

function isServerRendering() {
  return typeof document === 'undefined' || typeof window === 'undefined';
}

export const ScreenSizeContext = createContext(DEFAULT_SIZE.desktop);
ScreenSizeContext.displayName = 'ScreenSizeContext';

export interface Props {
  children: React.ReactNode;
}

export const ScreenSizeContextProvider: React.FC<Props> = ({ children }) => {
  const isMobile = useContext(MobileContext);
  const appEnv = useSelector(getEnv);
  const defaultNoHints = isMobile ? DEFAULT_SIZE.mobile : DEFAULT_SIZE.desktop;
  const measured = useMeasureElementCookieState(
    MeasuredCookieType.SCREEN_WIDTH,
    'both',
    DEFAULT_SIZE
  );
  let {
    containerSize = appEnv.clientHints
      ? { width: appEnv.clientHints.width, height: appEnv.clientHints.height }
      : defaultNoHints,
  } = measured;

  // Prefer client hint over cookie values on the server, this is not inside measured
  // itself because we have measurements that are not full viewport, hence we only
  // do this for the screensize measure
  if (isServerRendering() && appEnv.clientHints) {
    // Android has bug that initial pageloads can report its default 980 width
    // therefore if we see 980 we fallback to cookies
    if (isMobile && appEnv.clientHints.width === 980) {
      containerSize = measured.containerSize ?? defaultNoHints;
    } else {
      containerSize = {
        width: appEnv.clientHints.width,
        height: appEnv.clientHints.height,
      };
    }
  }

  // VisualViewport
  useVisualViewport(measured.updateContainerSize);

  return (
    <ScreenSizeContext.Provider value={containerSize}>
      {children}
    </ScreenSizeContext.Provider>
  );
};
ScreenSizeContextProvider.displayName = 'ScreenSizeContextProvider';
